import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import styles from '../../InpatientPortal.module.css';
import { useSelector } from 'react-redux';
import { formatValueByModalityId, getVitalUnit } from '../../../../utils/unitUtils';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);


const InpatientAuscChart = ({ vitalConfig, measurements, onPointClick }) => {
    const {dateFormat, unitType} = useSelector((state) => state.userInfo);
    const [scrollIndex, setScrollIndex] = useState(0);
    const itemsPerView = 9; 


    const visibleMeasurements = useMemo(() => {
        const start = scrollIndex;
        const end = Math.min(start + itemsPerView, measurements.length);
        return measurements.slice(start, end);
    }, [measurements, scrollIndex, itemsPerView]);

    const canScrollLeft = scrollIndex > 0;
    const canScrollRight = scrollIndex + itemsPerView < measurements.length;

    const handleScrollLeft = () => {
        if (canScrollLeft) {
            setScrollIndex(Math.max(0, scrollIndex - itemsPerView));
        }
    };

    const handleScrollRight = () => {
        if (canScrollRight) {
            setScrollIndex(Math.min(
                measurements.length - itemsPerView,
                scrollIndex + itemsPerView
            ));
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            const month = date.getMonth() + 1;
            const day = date.getDate();
            const year = date.getFullYear();
            return `${month}/${day}/${year}`;
        } catch {
            return dateString;
        }
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        try {
            const date = new Date(timestamp);
            const hour = date.getHours();
            const minute = date.getMinutes();
            const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
            const ampm = hour >= 12 ? 'PM' : 'AM';
            return `${hour12}:${String(minute).padStart(2, '0')}${ampm}`;
        } catch {
            return '';
        }
    };

    if (!vitalConfig || measurements.length === 0) {
        return null;
    }

    return (
        <div style={{display: 'flex', gap: '0', flexDirection: 'column', width: '100%'}}>
            {/* Chart Labels Section */}
            <div className={styles.ChartLabelsSection}>
                <div className={styles.MainLabel}>
                    <p className={styles.VitalNameText}>
                        {vitalConfig.title}
                    </p>
                </div>
            </div>

            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                width: '100%',
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '15px',
                backgroundColor: '#fff'
            }}>
                {/* Left Arrow */}
                <button
                    onClick={handleScrollLeft}
                    disabled={!canScrollLeft}
                    style={{
                        border: 'none',
                        background: canScrollLeft ? '#496ca3' : '#ccc',
                        color: '#fff',
                        borderRadius: '4px',
                        padding: '8px 12px',
                        cursor: canScrollLeft ? 'pointer' : 'not-allowed',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        minWidth: '40px'
                    }}
                >
                    ←
                </button>

                {/* Measurements Timeline */}
                <div style={{
                    display: 'flex',
                    gap: '15px',
                    flex: 1,
                    overflow: 'hidden',
                    justifyContent: 'center'
                }}>
                    {visibleMeasurements.map((measurement, index) => {

                        const dotColor = '#7A6B78';

                        return (
                            <div
                                key={`${measurement.timeStamp || measurement.date}-${index}`}
                                onClick={() => onPointClick && onPointClick(measurement, vitalConfig)}
                                style={{
                                    width: '100px',
                                    display: 'flex',
                                    gap: '8px',
                                    alignItems: 'center',
                                    flexDirection: 'column',
                                    cursor: onPointClick ? 'pointer' : 'default'
                                }}
                            >
                                <div
                                    style={{
                                        width: '12px',
                                        height: '12px',
                                        borderRadius: '50%',
                                        backgroundColor: dotColor,
                                        border: '2px solid #fff',
                                        boxShadow: '0 0 0 1px rgba(0,0,0,0.1)'
                                    }}
                                />
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '2px',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {measurement.timeStamp && (
                                        <span style={{ fontSize: '12px', fontWeight: '500' }}>
                                            {formatTime(measurement.timeStamp)}
                                        </span>
                                    )}
                                    <span style={{ fontSize: '11px', color: '#666' }}>
                                        {formatDate(measurement.date || measurement.formattedDate)}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Right Arrow */}
                <button
                    onClick={handleScrollRight}
                    disabled={!canScrollRight}
                    style={{
                        border: 'none',
                        background: canScrollRight ? '#496ca3' : '#ccc',
                        color: '#fff',
                        borderRadius: '4px',
                        padding: '8px 12px',
                        cursor: canScrollRight ? 'pointer' : 'not-allowed',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        minWidth: '40px'
                    }}
                >
                    →
                </button>
            </div>
        </div>
    );
};

export default InpatientAuscChart;

